import { z } from 'zod';
import { PCMP_CHANNELS, type PcmpChannel } from './pcmpProposal.types';
import {
  pcmpPageMetaSchema,
  pcmpQueueItemSchema,
  pcmpQueueSearchShape,
  type PcmpQueueFilters,
} from './pcmpQueue.types';

// Disburse Verify (API LB-1195, legacy LDS-770): MIS and Admin verify the
// proposals Accounts and Marketing paid out. A record only: final, it blocks
// nothing and moves no lane.

export const PCMP_DISBURSE_VERIFY_TABS = [
  'ACCOUNTS_PENDING',
  'MARKETING_PENDING',
  'ACCOUNTS_VERIFIED',
  'MARKETING_VERIFIED',
] as const;
export type PcmpDisburseVerifyTab = (typeof PCMP_DISBURSE_VERIFY_TABS)[number];

/** The Pending tab a channel's payouts are verified from. */
export const PCMP_DISBURSE_VERIFY_PENDING_TAB = {
  ACCOUNTS: 'ACCOUNTS_PENDING',
  MARKETING: 'MARKETING_PENDING',
} as const satisfies Record<PcmpChannel, PcmpDisburseVerifyTab>;

export const paginatedPcmpDisburseVerifyQueueSchema = z.object({
  data: z.array(pcmpQueueItemSchema.extend({ paymentSource: z.enum(PCMP_CHANNELS) })),
  meta: pcmpPageMetaSchema,
});

export type PaginatedPcmpDisburseVerifyQueue = z.infer<
  typeof paginatedPcmpDisburseVerifyQueueSchema
>;
export type PcmpDisburseVerifyItem = PaginatedPcmpDisburseVerifyQueue['data'][number];

export const pcmpDisburseVerifyCountsSchema = z.object({
  accountsPending: z.number(),
  marketingPending: z.number(),
  accountsVerified: z.number(),
  marketingVerified: z.number(),
});

export type PcmpDisburseVerifyCounts = z.infer<typeof pcmpDisburseVerifyCountsSchema>;

export interface PcmpDisburseVerifyQueueParams extends PcmpQueueFilters {
  tab: PcmpDisburseVerifyTab;
  page: number;
  limit: number;
}

export const pcmpDisburseVerifySearchSchema = z.object({
  tab: z.enum(PCMP_DISBURSE_VERIFY_TABS).optional().catch(undefined),
  ...pcmpQueueSearchShape,
});

export type PcmpDisburseVerifySearch = z.infer<typeof pcmpDisburseVerifySearchSchema>;

/** The ticked ids, or everything the Pending tab lists under the filters minus the unticked. */
export type PcmpDisburseVerifySelection =
  | { proposalIds: number[] }
  | { selectAllMatching: true; filters: PcmpQueueFilters; excludedProposalIds: number[] };

export type PcmpDisburseVerifyPayload = {
  tab: PcmpDisburseVerifyTab;
  remarks?: string | undefined;
} & PcmpDisburseVerifySelection;

export const pcmpDisburseVerifyResultSchema = z.object({
  requested: z.number(),
  verified: z.number(),
  /** No longer awaiting verification when the verify ran. */
  skipped: z.number(),
  proposalIds: z.array(z.coerce.number()),
});

export type PcmpDisburseVerifyResult = z.infer<typeof pcmpDisburseVerifyResultSchema>;
